(() => {
  if (customElements.get('free-shipping-bar')) return;

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold || 0);
      if (!this.threshold) return;

      this.message = this.querySelector('[data-shipping-message]');
      this.progress = this.querySelector('[data-shipping-progress]');
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onExternalRefresh = this.onExternalRefresh.bind(this);
      this.onCartMutation = this.onCartMutation.bind(this);
      this.render(Number(this.dataset.cartTotal || 0));

      // Dawn/Taste keeps these helpers as global lexical bindings, not as
      // window properties. Using window.subscribe meant the bar never heard
      // normal AJAX add-to-cart or quantity-change events.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }
      document.addEventListener('cart:refresh', this.onExternalRefresh);
      // Some app-based quick-add buttons and older theme controls do not
      // publish Dawn's cart-update event. These browser events cover those
      // paths without depending on a specific app or cart presentation.
      ['cart:updated', 'cart:change', 'cart:added', 'product:added'].forEach((eventName) => {
        document.addEventListener(eventName, this.onCartMutation);
      });

      // The cart bubble is updated by every standard cart path. Watching it
      // gives the bar a reliable final fallback when a third-party control
      // updates the cart silently.
      const cartBubble = document.getElementById('cart-icon-bubble');
      if (cartBubble && 'MutationObserver' in window) {
        this.cartBubbleObserver = new MutationObserver(this.onCartMutation);
        this.cartBubbleObserver.observe(cartBubble, { childList: true, subtree: true, characterData: true });
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      document.removeEventListener('cart:refresh', this.onExternalRefresh);
      ['cart:updated', 'cart:change', 'cart:added', 'product:added'].forEach((eventName) => {
        document.removeEventListener(eventName, this.onCartMutation);
      });
      this.cartBubbleObserver?.disconnect();
      window.clearTimeout(this.refreshTimer);
    }

    onCartUpdate(event = {}) {
      const cart = event.cartData || event.cart;
      if (cart && typeof cart.total_price !== 'undefined') {
        this.render(Number(cart.total_price));
      } else {
        this.refresh();
      }
    }

    onExternalRefresh() { this.refresh(); }

    onCartMutation() {
      // Let the Cart API finish before reading its authoritative total. The
      // tiny debounce consolidates a quantity control's multiple DOM changes.
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refresh(), 0);
    }

    async refresh() {
      try {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Cart refresh failed');
        const cart = await response.json();
        this.render(Number(cart.total_price || 0));
      } catch (error) {
        // The initial Liquid-rendered state remains visible if the request fails.
        console.warn('Free shipping bar could not refresh.', error);
      }
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney && this.dataset.moneyFormat) {
        return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
      }
      const currency = this.dataset.currency || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    }

    render(total) {
      const qualified = total >= this.threshold;
      const remaining = Math.max(this.threshold - total, 0);
      const showRemaining = this.dataset.showRemaining === 'true';
      const thresholdText = this.formatMoney(this.threshold);
      const remainingText = this.formatMoney(remaining);
      let text;

      if (qualified) {
        text = this.dataset.successMessage;
      } else if (showRemaining) {
        text = (this.dataset.remainingMessage || '').replace('[amount]', remainingText);
      } else {
        text = (this.dataset.defaultMessage || '').replace('[amount]', thresholdText);
      }

      this.classList.toggle('is-qualified', qualified);
      this.classList.toggle('has-cart-value', total > 0);
      if (this.message) this.message.textContent = text;
      if (this.progress) this.progress.style.width = `${Math.min((total / this.threshold) * 100, 100)}%`;
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
})();
